<template>
  <page-stepped
    :title="title"
    :breadcrumbs="breadcrumbs"
    :steps="steps"
    :model="model"
    :fetch-endpoint="fetchAutomationsEndpoint"
    :save-endpoint="saveEndpoint"
    :after-save="afterSave"
    :rules="rules"
    :is-edit="isEditPage"
    :readonly="readonly"
    @clear-errors="clearErrors"
  />
</template>

<script>
import { projectEndpoint, projectRoute } from '@/core/helpers'
import { TargetLabel } from '@/modules/automations/enum/TargetEnum'
import { FrequencyTypeLabel } from '@/modules/automations/enum/RecurrenceEnum'
import StepExportExecutionSchedule from '@/modules/automations/components/StepExportExecutionSchedule'
import PageStepped from '@/common/components/page/PageStepped'
import StepExportDataSource from '@/modules/automations/components/StepExportDataSource'
import StepExportDataStructure from '@/modules/automations/components/StepExportDataStructure'
import RestApi from '@/core/services/RestApi'
import _ from 'lodash'
import { Meta, Google } from '@/modules/automations/enum/DataStructureScheme'
import iframeTitleMixin from '@/common/mixins/iframeTitleMixin'

export default {
  name: 'ExportFormPage',

  mixins: [iframeTitleMixin],

  components: {
    PageStepped
  },

  data() {
    return {
      model: {},
      segments: [],
      integrations: [],
      TargetLabel,
      FrequencyTypeLabel,
      nextButton: {},
      finalStep: false,
      isAllowedToContinue: false,
      steps: [],
      allSteps: [
        {
          name: 'export_type',
          title: this.$t('Automations_Tab_ExportType'),
          component: StepExportDataSource,
          completed: false,
          model: this.model
        },
        {
          name: 'data_structure',
          title: this.$t('Automations_Tab_DataStructure'),
          component: StepExportDataStructure,
          completed: false,
          model: this.model,
          bindProps: {
            dataStructureErrors: this.dataStructureErrors
          }
        },
        {
          name: 'data_schedule',
          title: this.$t('Automations_Title_Schedule'),
          component: StepExportExecutionSchedule,
          completed: false,
          model: this.model
        }
      ],
      validateAutomationName: function(rule, value, callback) {
        let endpoint = projectEndpoint({
          method: 'get',
          url: `export-automations`,
          params: {
            name: value
          }
        })
        if (value !== '') {
          RestApi(endpoint)
            .then(({ data }) => {
              if (data.map(e => e.name).some(e => e === value) === false) {
                callback()
              } else {
                if (this.automationId !== null) {
                  const matchingId = data.find(e => e.name === value).id
                  if (matchingId === parseInt(this.automationId)) {
                    callback()
                  } else {
                    callback(new Error())
                  }
                } else {
                  callback(new Error())
                }
              }
            })
            .catch(error => {
              this.$message({
                message: error.message,
                type: 'error'
              })
            })
        }
      },
      dataStructureErrors: []
    }
  },

  created() {
    this.steps = [...this.allSteps]
    if (!this.isEditPage) {
      this.model = {
        name: '',
        active: 1,
        type: 'segment',
        config: {
          identity: 'profile',
          attributes: [],
          fields: null,
          segment_id: null,
          integration_id: null,
          delta: 0,
          attribute_timestamps_enabled: false
        },
        target: {
          type: null
        },
        schedule: {
          frequency: 'daily',
          time: '00:00',
          minutes: null
        }
      }
    }
  },

  computed: {
    embeddedPageTitle() {
      return this.isEditPage && this.model ? this.model.name : null
    },

    stepsCompleted() {
      return this.steps.every(element => element.completed === true)
    },
    breadcrumbs() {
      return [
        {
          label: 'Data'
        },
        {
          label: this.$t('Automations_Breadcrumb_Automations')
        },
        {
          label: this.$t('Automations_Tab_Exports'),
          route: projectRoute({
            name: 'AutomationExports'
          })
        },
        { label: this.title }
      ]
    },

    label() {
      if (this.isEditPage) {
        return this.$t('Audiences_Breadcrumb_Automation')
      } else {
        return this.$t('Audiences_Breadcrumb_CreateAutomation')
      }
    },

    title() {
      if (this.$store.getters.embeddedTitle) {
        return this.$store.getters.embeddedTitle
      }
      if (this.isEditPage) {
        return this.model.hasOwnProperty('name') ? this.model.name : ''
      } else {
        return this.$t('Automations_Title_CreateAutomation')
      }
    },

    rules() {
      return {
        name: [
          {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          {
            validator: this.validateAutomationName,
            message: `${this.model.name} ${this.$t(
              'RestForm_FieldNameExists'
            )}.`,
            automationId: this.$route.query.id ? this.$route.query.id : null
          }
        ],
        config: {
          audience_id: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          segment_id: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          integration_id: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          identity: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          attributes: [
            { validator: this.validateColumnAttributes, trigger: 'blur' }
          ]
        },
        target: {
          type: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          integration_id: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          emails: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          }
        }
      }
    },
    fetchAutomationsEndpoint() {
      if (this.isEditPage) {
        return projectEndpoint({
          method: 'get',
          url: `export-automations/${this.$route.query.id}`,
          handler: data => {
            this.model = data
            if (
              this.model.config.identity === 'campaign' &&
              !this.model.config.hasOwnProperty('fields')
            ) {
              this.model.config.fields = null
            }
          }
        })
      } else {
        return null
      }
    },

    saveEndpoint() {
      if (this.isEditPage) {
        return projectEndpoint({
          method: 'patch',
          url: `export-automations/${this.$route.query.id}`
        })
      } else {
        return projectEndpoint({
          method: 'post',
          url: `export-automations`
        })
      }
    },

    isEditPage() {
      return !!this.$route.query.id
    },

    automationStatus() {
      if (!this.isEditPage) {
        return null
      } else if (this.model.active === true || this.model.active === 1) {
        return {
          type: 'success',
          label: this.$t('Automations_Field_StatusActive')
        }
      } else {
        return {
          type: 'info',
          label: this.$t('Automations_Field_StatusInactive')
        }
      }
    },

    readonly() {
      return (
        !this.$can('manageAutomation') ||
        this.$route.name === 'AutomationExportView'
      )
    }
  },

  methods: {
    afterSave() {
      this.$router.push(
        projectRoute({
          name: 'AutomationExports'
        })
      )
    },

    validateColumnAttributes(rule, value, callback) {
      if (['google_ads', 'fb_ads'].includes(this.model.target.type)) {
        if (!value.some(column => column.attribute !== null)) {
          // Error message will be shown on a banner not the normal form validation error message
          if (
            !this.dataStructureErrors.includes(
              'Automations_Field_Integration_Should_Have_Mapping'
            )
          ) {
            this.dataStructureErrors.push(
              'Automations_Field_Integration_Should_Have_Mapping'
            )
            this.$set(
              this.steps[1].bindProps,
              'dataStructureErrors',
              this.dataStructureErrors
            )
          }
          return
        }
      }
      let mappingErrorIndex = this.dataStructureErrors.indexOf(
        'Automations_Field_Integration_Should_Have_Mapping'
      )
      this.dataStructureErrors.splice(mappingErrorIndex, 1)
      return callback()
    },

    clearErrors() {
      // This has been done to mimic element ui errors, when moving back to the previous tab the errors should be removed
      if (['google_ads', 'fb_ads'].includes(this.model.target.type)) {
        this.dataStructureErrors = []
        this.$set(
          this.steps[1].bindProps,
          'dataStructureErrors',
          this.dataStructureErrors
        )
      }
    }
  },

  watch: {
    'model.target.type': {
      deep: true,
      handler(type, oldType) {
        if (type === 'email') {
          delete this.model.target.integration_id
          if (this.model.target.emails === undefined) {
            this.$set(this.model.target, 'emails', [])
          }
        }

        if (type === 'fs') {
          delete this.model.target.emails
          if (this.model.target.integration_id === undefined) {
            this.$set(this.model.target, 'integration_id', null)
          }
        }

        if (['google_ads', 'fb_ads'].includes(oldType)) {
          // attributes format is different for Facebook and Google Ads
          this.model.config.attributes = []
          delete this.model.config.audience_id
        }

        if (type === 'fb_ads') {
          this.model.config.identity = 'profile'
          if (oldType || !this.$route.query.id) {
            this.model.config.attributes = _.cloneDeep(Meta)
            this.$set(this.model.config, 'audience_id', null)
          }
        }

        if (type === 'google_ads') {
          this.model.config.identity = 'profile'
          if (oldType || !this.$route.query.id) {
            this.model.config.attributes = _.cloneDeep(Google)
            this.$set(this.model.config, 'audience_id', null)
          }
        }

        if (type === '') {
          this.model.target.type = null
          delete this.model.target.emails
          delete this.model.target.integration_id
        }
      }
    },
    'model.config.attributes': {
      deep: true,
      handler(value) {
        if (value.some(column => column.attribute !== null)) {
          let mappingErrorIndex = this.dataStructureErrors.indexOf(
            'Automations_Field_Integration_Should_Have_Mapping'
          )
          this.dataStructureErrors.splice(mappingErrorIndex, 1)
        }
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/automations/pages/ExportFormPage.vue